import { buildGeo, type GeoJson } from "@beacon/client-core";

/**
 * The private control-plane gate: sign an envelope and POST its DAG-CBOR wire
 * bytes to an intern-only service (moderation flag/appeal, …), throwing on a
 * non-2xx. These envelopes NEVER hit the public firehose — the receiving service
 * verifies the signature against the envelope's own signingKey (no relay chain),
 * which is why a wiped relay never breaks them and they need no genesis self-heal.
 *
 * Helper availability (matcher-transport) shares the shape but goes through its
 * own typed MatcherClient ack, so it stays separate.
 */
type SignClient = {
  sign(input: { $type: string; payload: Record<string, unknown>; geo: GeoJson }): Promise<{
    wire: { id: string; bytes: Uint8Array };
  }>;
};

export interface PostSignedInput {
  $type: string;
  payload: Record<string, unknown>;
  cell: string;
}

/** Sign `{ $type, payload }` at `cell` and POST the wire bytes to `postUrl`.
 *  Returns the signed envelope (callers that need the TID read `wire.id`). */
export async function postSignedToService(
  client: SignClient,
  input: PostSignedInput,
  postUrl: string,
  fetchImpl: typeof fetch = fetch,
  label: string = input.$type,
): Promise<{ wire: { id: string; bytes: Uint8Array } }> {
  const env = await client.sign({ $type: input.$type, payload: input.payload, geo: buildGeo(input.cell) });
  const resp = await fetchImpl(postUrl, {
    method: "POST",
    headers: { "Content-Type": "application/dag-cbor" },
    body: env.wire.bytes as BodyInit,
  });
  if (!resp.ok) throw new Error(`${label} POST failed: ${resp.status}`);
  return env;
}
